/** Helpers de dinero — todas las cifras viajan en céntimos por la red. */
import { currentLocaleTag } from './formatting'

// es-ES no agrupa por defecto los números de 4 cifras (7500 → "7500"); 'always'
// fuerza el separador de miles en todos ("7.500,00"). El valor 'always' aún no
// está en el tipo de TS (ES2023) pero los navegadores lo soportan.
const GROUP_ALWAYS = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—'
  return new Intl.NumberFormat(currentLocaleTag(), {
    style: 'currency',
    currency: 'EUR',
    ...GROUP_ALWAYS,
  }).format(cents / 100)
}

/**
 * Convierte un string del input a céntimos enteros. Independiente del idioma:
 * centsToEurosInput formatea según la UI ("900,00" en es, "900.00" en en), así
 * que aquí NO se puede asumir la convención española (punto=miles) — eso leía
 * "900.00" como 90.000 €. Reglas:
 *  - ambos separadores → el ÚLTIMO es el decimal ("17.000,50" y "17,000.50");
 *  - un solo separador repetido, o con grupo final de 3 dígitos → miles
 *    ("1.234.567", "17.000", "90,000");
 *  - un solo separador con 1-2 dígitos detrás → decimal ("900.00", "1,5").
 */
function normalizeNumericInput(input: string): string {
  let s = input.replace(/[\s€]/g, '')
  const dots = (s.match(/\./g) ?? []).length
  const commas = (s.match(/,/g) ?? []).length
  if (dots > 0 && commas > 0) {
    const decSep = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ','
    const thouSep = decSep === '.' ? ',' : '.'
    s = s.split(thouSep).join('').replace(decSep, '.')
  } else if (dots + commas > 0) {
    const sep = dots > 0 ? '.' : ','
    const count = dots > 0 ? dots : commas
    const tail = s.slice(s.lastIndexOf(sep) + 1)
    if (count > 1 || /^\d{3}$/.test(tail)) {
      s = s.split(sep).join('')
    } else {
      s = s.replace(sep, '.')
    }
  }
  return s
}

export function parseEurosToCents(input: string): number {
  if (!input.trim()) return 0
  const n = Number(normalizeNumericInput(input))
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100)
}

/**
 * Lo mismo, pero para CANTIDADES que no son dinero: kilómetros, días, unidades.
 *
 * Existe porque `Number(x.replace(',', '.'))` —el atajo obvio— se come dos casos
 * que el usuario teclea a diario: "1.234" da 1,234 (mil veces menos, y al
 * recargar el campo vuelve a mostrar "1.234", así que la revisión visual no lo
 * ve) y "1.234,5" da NaN. Y la propia app entrena a escribir el punto como
 * separador de miles, porque así formatea `centsToEurosInput`.
 */
export function parseQuantity(input: string): number {
  if (!input.trim()) return 0
  const n = Number(normalizeNumericInput(input))
  return Number.isFinite(n) ? n : 0
}

/** Convierte céntimos a string editable con separadores de miles: "17.000,50". */
export function centsToEurosInput(cents: number): string {
  if (!cents) return ''
  return (cents / 100).toLocaleString(currentLocaleTag(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...GROUP_ALWAYS,
  })
}
